import { InfoCircleFilled, LoadingOutlined } from '@ant-design/icons'
import { getAllStores, ITymberEcommStore } from '@getgreenline/blaze-ecomm'
import { ExternalSourceAPI } from '@getgreenline/external-sources'
import { ILocation, PermissionNames } from '@getgreenline/homi-shared'
import { Button, Col, Empty, Row, Table, Tooltip } from 'antd'
import { Alert, Spin } from 'antd-v4'
import { ColumnProps } from 'antd/lib/table'
import { inject } from 'mobx-react'
import React, { FC, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { AppMainView, AppView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { FeatureDisabledResult } from '../../../../components/FeatureDisabledResult'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import Endpoints from '../../../../constants/Endpoints'
import { ExternalLinks, HelpLinks } from '../../../../constants/ExternalLinks'
import { FeatureToggle } from '../../../../constants/FeatureToggles'
import { BLAZE_ECOMMERCE } from '../../../../constants/general'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { useFeature } from '../../../../utilities/hooks/useFeature'
import { ConfigurationDrawer } from './ConfigurationDrawer'
import { OnlineMenuModal } from './OnlineMenuModal'
import { RetailerConfigSwitch } from './RetailerConfigSwitch'
import './styles.scss'
import { BlazeAdminDeliveryToggle } from './BlazeAdminDeliveryToggle'
import { observer } from 'mobx-react-lite'
import {
  EcommerceSettingsFields,
  IRetailerToggleMessage,
  LocationWithConfig,
  MessageType,
} from './EcommerceSettingModels'

enum EcomColumnHeader {
  LOCATIONS = 'Locations',
  ENABLE_DISABLE = 'Enable/Disable',
  ECOM_DELIVERY = 'Delivery',
  CONFIGURATION = 'Configuration',
}

enum EcomColumnHeaderTooltip {
  ENABLE_DISABLE = 'This will enable BLAZE ECOM for the location.',
  ECOM_DELIVERY = 'This will enable BLAZE ECOM Delivery for the location',
}

interface ColumnHeaderProps {
  title: string
  tooltipText: string
}

const ColumnHeader: FC<ColumnHeaderProps> = ({ title, tooltipText }: ColumnHeaderProps) => {
  return (
    <span>
      <span className='mr-2'>{title}</span>
      <Tooltip placement='right' overlayStyle={{ minWidth: 'fit-content' }} title={tooltipText}>
        <InfoCircleFilled />
      </Tooltip>
    </span>
  )
}

interface EcommerceSettingsProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const EcommerceSettingsComponent: FC<EcommerceSettingsProps> = ({
  currentCompanyStore,
  currentUserStore,
}: EcommerceSettingsProps) => {
  const [selectedDrawerLocation, setSelectedDrawerLocation] =
    useState<LocationWithConfig | null>(null)
  const [selectedModalLocation, setSelectedModalLocation] =
    useState<LocationWithConfig | null>(null)
  const [isOnlineMenuModalVisible, setIsOnlineMenuModalVisible] = useState(false)

  const [tenantConfig, setTenantConfig] = useState<ITymberEcommStore[]>([])
  const [isWaitingUpdate, setIsWaitingUpdate] = useState(false)
  const [isWaitingInitialFetch, setIsWaitingInitialFetch] = useState(true)
  const [retailerConfigMessage, setRetailerConfigMessage] =
    useState<IRetailerToggleMessage | null>(null)
  const [connectMerrcoMessage, setConnectMerrcoMessage] = useState<string | null>(null)

  const featureIsEnabled = useFeature(
    FeatureToggle.CAN_VIEW_ECOMMERCE_SETTINGS,
    currentCompanyStore,
  )
  const onlineOrderingIsEnabled = useFeature(
    FeatureToggle.ONLINE_ORDERING_MODULE,
    currentCompanyStore,
  )

  const canUseKMLFiles = useFeature(FeatureToggle.CAN_USE_KML_FILES, currentCompanyStore)

  const userHasPermissions =
    currentCompanyStore?.company?.id &&
    currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_COMPANY_DETAILS,
      currentCompanyStore.company.id,
    )
  const userIsGreenlineAdmin = currentUserStore?.isAdmin

  const { FORM_ONLINE_ORDERING_WAITLIST } = ExternalLinks
  const antIcon = <LoadingOutlined spin />
  const defaultColumnWidth = '33%'

  const locations =
    currentCompanyStore?.locations?.map((location) => {
      const config = getLocationConfig(location)
      return { ...location, config, key: location.id }
    }) ?? []

  useEffect(() => {
    async function fetchConfig() {
      if (currentCompanyStore?.company?.id) {
        const tenantConfig = await getAllStores(
          Endpoints.AUTHZ_GATEWAY_URL,
          currentCompanyStore?.company?.id.toString(),
        )
        setTenantConfig(tenantConfig.data)
        setIsWaitingInitialFetch(false)
      }
    }
    async function enableBlazeEcommerceIntegration() {
      if (currentCompanyStore?.company?.id) {
        const companyId = currentCompanyStore.company.id
        const externalSources = await ExternalSourceAPI.getCompanyExternalSources(companyId)

        const blazeEcommerceIntegration = externalSources.find(
          (externalSource) => externalSource.name === BLAZE_ECOMMERCE,
        )

        if (blazeEcommerceIntegration && !blazeEcommerceIntegration.isEnabled) {
          await ExternalSourceAPI.editCompanyExternalSource(companyId, {
            ...blazeEcommerceIntegration,
            isEnabled: true,
          })
        }
      }
    }

    if (userIsGreenlineAdmin || userHasPermissions) fetchConfig()
    enableBlazeEcommerceIntegration()
  }, [])

  function getLocationConfig(location: ILocation) {
    const locationConfig = tenantConfig.find(
      (_location) => _location.greenlineAttributes.retailerId === location.id.toString(),
    )
    return locationConfig
  }

  function locallyUpdateConfig(updatedConfig: ITymberEcommStore) {
    const newTenantConfig = [...tenantConfig]
    const indexToReplace = newTenantConfig.findIndex(
      (retailer) =>
        retailer.greenlineAttributes.retailerId === updatedConfig.greenlineAttributes.retailerId,
    )
    if (indexToReplace >= 0) {
      newTenantConfig.splice(indexToReplace, 1, updatedConfig)
    } else {
      newTenantConfig.push(updatedConfig)
    }
    setTenantConfig(newTenantConfig)
  }

  function handleConfigurationBtnClick(location: LocationWithConfig) {
    setSelectedDrawerLocation(location)
  }

  const getRetailerToggleError = (
    field: EcommerceSettingsFields,
  ): IRetailerToggleMessage | null => {
    return retailerConfigMessage?.field === field &&
      retailerConfigMessage.type === MessageType.ERROR
      ? retailerConfigMessage
      : null
  }

  const columns: ColumnProps<LocationWithConfig>[] = [
    {
      title: EcomColumnHeader.LOCATIONS,
      dataIndex: EcommerceSettingsFields.NAME,
      key: EcommerceSettingsFields.NAME,
      width: defaultColumnWidth,
    },
    {
      title: (
        <ColumnHeader
          title={EcomColumnHeader.ENABLE_DISABLE}
          tooltipText={EcomColumnHeaderTooltip.ENABLE_DISABLE}
        />
      ),
      dataIndex: EcommerceSettingsFields.ONLINE_MENU,
      key: EcommerceSettingsFields.ONLINE_MENU,
      width: '20%',
      align: 'center' as const,
      render: (text: string, record: LocationWithConfig) => {
        if (userHasPermissions && isWaitingInitialFetch) return <Spin indicator={antIcon} />
        const retailerToggleError = getRetailerToggleError(EcommerceSettingsFields.ONLINE_MENU)
        return (
          <RetailerConfigSwitch
            locations={locations}
            locationId={record?.id}
            disabled={!userIsGreenlineAdmin}
            retailerToggleError={retailerToggleError}
            isWaitingUpdate={isWaitingUpdate}
            locallyUpdateConfig={locallyUpdateConfig}
            setIsModalVisible={setIsOnlineMenuModalVisible}
            useModalLocationState={[selectedModalLocation, setSelectedModalLocation]}
          />
        )
      },
    },

    {
      title: EcomColumnHeader.CONFIGURATION,
      dataIndex: EcommerceSettingsFields.CONFIGURATION,
      key: EcommerceSettingsFields.CONFIGURATION,
      width: defaultColumnWidth,
      align: 'right' as const,
      render: (text: string, record: LocationWithConfig) => {
        return (
          <Button
            disabled={!userHasPermissions || !record.config?.attributes.isActive}
            onClick={() => {
              if (record) return handleConfigurationBtnClick(record)
            }}
          >
            Configure
          </Button>
        )
      },
    },
  ]

  const deliveryToggleColumn: ColumnProps<LocationWithConfig> = {
    title: (
      <ColumnHeader
        title={EcomColumnHeader.ECOM_DELIVERY}
        tooltipText={EcomColumnHeaderTooltip.ECOM_DELIVERY}
      />
    ),
    key: EcomColumnHeader.ECOM_DELIVERY,
    width: '20%',
    align: 'center' as const,
    render: (text: string, record: LocationWithConfig) => {
      return userHasPermissions && isWaitingInitialFetch ? (
        <Spin indicator={antIcon} />
      ) : (
        <BlazeAdminDeliveryToggle
          isUserBlazeAdmin={userIsGreenlineAdmin}
          isWaitingBlazeEcomUpdate={isWaitingUpdate}
          location={record}
          locallyUpdateConfig={locallyUpdateConfig}
        />
      )
    },
  }

  columns.splice(2, 0, deliveryToggleColumn)

  const tableColumns = onlineOrderingIsEnabled
    ? columns
    : columns.filter(
        (column) =>
          column.key !== EcommerceSettingsFields.ONLINE_ORDERING &&
          column.key !== EcommerceSettingsFields.ACCEPTING_ORDERS,
      )

  return (
    <AppView column>
      <AppHeader title='BLAZE ECOM'>
        <HelpLinkMenu
          helpLinkMenus={{
            href: HelpLinks.ECOM,
            title: 'Setting up BLAZE ECOM',
          }}
        />
      </AppHeader>

      <AppMainView className='background-grey intercom-pb-2'>
        {featureIsEnabled === null ? (
          <LoadingAnimation />
        ) : !featureIsEnabled ? (
          <FeatureDisabledResult />
        ) : (
          <>
            {!userHasPermissions ? (
              <Alert
                type='error'
                message={`Your account does not have the "Can edit company details" permission. Please ask your administrator for access.`}
                style={{ marginBottom: 8 }}
              />
            ) : null}
            <Alert
              type='warning'
              message={
                <p style={{ marginBottom: 0 }}>
                  Interested in signing up? Please fill out this
                  <a href={FORM_ONLINE_ORDERING_WAITLIST} target='_blank' rel='noreferrer'>
                    {' '}
                    form
                  </a>
                  .
                </p>
              }
              style={{ marginBottom: 8 }}
            />
            {retailerConfigMessage ? (
              <Alert
                message={<div>{retailerConfigMessage.message}</div>}
                type={retailerConfigMessage.type}
                showIcon
                style={{ marginBottom: 16 }}
                closable
                onClose={() => {
                  setRetailerConfigMessage(null)
                }}
              />
            ) : null}
            {connectMerrcoMessage ? (
              <Alert
                type='success'
                message={connectMerrcoMessage}
                style={{ marginBottom: 16 }}
                showIcon
                closable
                onClose={() => {
                  setConnectMerrcoMessage(null)
                }}
              />
            ) : null}

            <Row className='white-rounded-container'>
              <Helmet>
                <title>BLAZE ECOM</title>
                <meta name='page-loaded' content='true' />
              </Helmet>

              <Row>
                <Col>
                  {!locations || locations?.length === 0 ? (
                    <Empty description='No locations available' />
                  ) : (
                    <Table columns={tableColumns} dataSource={locations} pagination={false} />
                  )}
                </Col>
              </Row>
            </Row>

            {currentCompanyStore && selectedDrawerLocation ? (
              <ConfigurationDrawer
                selectedLocation={selectedDrawerLocation}
                setSelectedLocation={setSelectedDrawerLocation}
                locallyUpdateConfig={locallyUpdateConfig}
                onlineOrderingIsEnabled={!!onlineOrderingIsEnabled}
                setConnectMerrcoMessage={setConnectMerrcoMessage}
                canUseKMLFiles={canUseKMLFiles}
              />
            ) : null}
            <OnlineMenuModal
              visible={isOnlineMenuModalVisible}
              closeModal={() => setIsOnlineMenuModalVisible(false)}
              setIsWaitingUpdate={setIsWaitingUpdate}
              selectedLocation={selectedModalLocation}
              locallyUpdateConfig={locallyUpdateConfig}
              setRetailerToggleMessage={setRetailerConfigMessage}
            />
          </>
        )}
      </AppMainView>
    </AppView>
  )
}

export const EcommerceSettings = inject(
  'currentCompanyStore',
  'currentUserStore',
)(observer(EcommerceSettingsComponent))
